import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withSpring,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import { theme } from '@/constants/theme';
import { loadPracticeCenterData, pickQuickLessons } from '@/features/unified-learning/runtime/practiceCenterService';
import { RuntimeSessionGuard } from '@/features/unified-learning/runtime/screens/RuntimeSessionGuard';
import {
  buildRuntimeExamPath,
  buildRuntimePracticeSessionPath,
  buildRuntimeReviewPath,
  buildRuntimeReversePath,
  type RuntimeBase,
} from '@/features/unified-learning/runtime/runtimeRoutes';
import BubbleMenu from '@/components/premium/BubbleMenu';
import MagicBento from '@/components/premium/MagicBento';
import ClickSpark from '@/components/effects/ClickSpark';

const BG = '#0B0B14';
const SURFACE = '#16142A';
const BORDER = '#2D2B50';
const TEXT = '#F0EFF8';
const TEXT_MED = '#9896B4';
const PRIMARY = theme.colors.primary;
const SUCCESS = '#10B981';

function PulseLoader({ color = PRIMARY }: { color?: string }) {
  const hex = color.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16) || 59;
  const g = parseInt(hex.slice(2, 4), 16) || 130;
  const b = parseInt(hex.slice(4, 6), 16) || 246;

  const ring1Scale = useSharedValue(0.4);
  const ring1Opacity = useSharedValue(0.8);
  const ring2Scale = useSharedValue(0.4);
  const ring2Opacity = useSharedValue(0.6);
  const coreScale = useSharedValue(1);

  useEffect(() => {
    ring1Scale.value = withRepeat(
      withSequence(
        withTiming(1, { duration: 900, easing: Easing.out(Easing.cubic) }),
        withTiming(0.4, { duration: 0 }),
      ),
      -1,
      false,
    );
    ring1Opacity.value = withRepeat(
      withSequence(
        withTiming(0, { duration: 900, easing: Easing.out(Easing.cubic) }),
        withTiming(0.8, { duration: 0 }),
      ),
      -1,
      false,
    );
    ring2Scale.value = withDelay(
      300,
      withRepeat(
        withSequence(
          withTiming(1, { duration: 900, easing: Easing.out(Easing.cubic) }),
          withTiming(0.4, { duration: 0 }),
        ),
        -1,
        false,
      ),
    );
    ring2Opacity.value = withDelay(
      300,
      withRepeat(
        withSequence(
          withTiming(0, { duration: 900, easing: Easing.out(Easing.cubic) }),
          withTiming(0.6, { duration: 0 }),
        ),
        -1,
        false,
      ),
    );
    coreScale.value = withRepeat(
      withSequence(
        withTiming(1.2, { duration: 650, easing: Easing.inOut(Easing.sin) }),
        withTiming(0.85, { duration: 650, easing: Easing.inOut(Easing.sin) }),
      ),
      -1,
      false,
    );
  }, []);

  const r1Style = useAnimatedStyle(() => ({
    opacity: ring1Opacity.value,
    transform: [{ scale: ring1Scale.value }],
    ...(Platform.OS === 'web'
      ? ({ boxShadow: `0 0 ${Math.round(ring1Opacity.value * 22)}px rgba(${r},${g},${b},0.5)` } as any)
      : {}),
  }));
  const r2Style = useAnimatedStyle(() => ({
    opacity: ring2Opacity.value,
    transform: [{ scale: ring2Scale.value }],
  }));
  const coreStyle = useAnimatedStyle(() => ({
    transform: [{ scale: coreScale.value }],
    ...(Platform.OS === 'web'
      ? ({ boxShadow: `0 0 14px rgba(${r},${g},${b},0.7)` } as any)
      : {}),
  }));

  return (
    <View style={ls.wrap}>
      <Animated.View style={[ls.ring, { borderColor: `rgba(${r},${g},${b},0.6)` }, r1Style]} />
      <Animated.View style={[ls.ring2, { borderColor: `rgba(${r},${g},${b},0.35)` }, r2Style]} />
      <Animated.View style={[ls.core, { backgroundColor: color }, coreStyle]} />
    </View>
  );
}

const ls = StyleSheet.create({
  wrap: { width: 52, height: 52, alignItems: 'center', justifyContent: 'center' },
  ring: {
    position: 'absolute',
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1.5,
    top: 4,
    left: 4,
  },
  ring2: {
    position: 'absolute',
    width: 52,
    height: 52,
    borderRadius: 26,
    borderWidth: 1,
    top: 0,
    left: 0,
  },
  core: {
    width: 13,
    height: 13,
    borderRadius: 6.5,
  },
});

type ModeCardProps = {
  icon: string;
  title: string;
  subtitle: string;
  cta: string;
  badge?: string;
  disabled?: boolean;
  onPress: () => void;
  mountDelay?: number;
};

function ModeCard({ icon, title, subtitle, cta, badge, onPress, disabled, mountDelay = 0 }: ModeCardProps) {
  const mountY = useSharedValue(16);
  const mountOpacity = useSharedValue(0);
  const pressScale = useSharedValue(1);
  const hoverElevation = useSharedValue(0);

  useEffect(() => {
    mountOpacity.value = withDelay(mountDelay, withTiming(1, { duration: 360, easing: Easing.out(Easing.cubic) }));
    mountY.value = withDelay(mountDelay, withSpring(0, { damping: 18, stiffness: 130 }));
  }, [mountDelay]);

  const cardStyle = useAnimatedStyle(() => ({
    opacity: mountOpacity.value,
    transform: [{ translateY: mountY.value }, { scale: pressScale.value }],
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: `0 ${4 + hoverElevation.value * 10}px ${14 + hoverElevation.value * 18}px rgba(0,0,0,${0.25 + hoverElevation.value * 0.2})`,
        } as any)
      : {}),
  }));

  return (
    <Animated.View style={cardStyle}>
      <ClickSpark onPress={onPress} disabled={disabled}>
        <Pressable
          disabled={disabled}
          style={[s.modeCard, disabled && { opacity: 0.55 }]}
          onPress={onPress}
          onHoverIn={() => {
            hoverElevation.value = withTiming(1, { duration: 200 });
            pressScale.value = withSpring(1.01, { damping: 16, stiffness: 200 });
          }}
          onHoverOut={() => {
            hoverElevation.value = withTiming(0, { duration: 300 });
            pressScale.value = withSpring(1, { damping: 16, stiffness: 200 });
          }}
          onPressIn={() => {
            pressScale.value = withSpring(0.97, { damping: 16, stiffness: 300 });
          }}
          onPressOut={() => {
            pressScale.value = withSpring(1, { damping: 14, stiffness: 220 });
          }}
        >
          <View style={s.modeHeader}>
            {!!badge && (
              <View style={s.badge}>
                <Text style={s.badgeText}>{badge}</Text>
              </View>
            )}
            <Text style={s.modeIcon}>{icon}</Text>
          </View>
          <Text style={s.modeTitle}>{title}</Text>
          <Text style={s.modeSubtitle}>{subtitle}</Text>
          <Text style={s.modeCta}>{cta}</Text>
        </Pressable>
      </ClickSpark>
    </Animated.View>
  );
}

function QuickRow({
  quick5,
  quick10,
  onQuick5,
  onQuick10,
}: {
  quick5: string[];
  quick10: string[];
  onQuick5: () => void;
  onQuick10: () => void;
}) {
  const mountOpacity = useSharedValue(0);
  const mountY = useSharedValue(14);

  useEffect(() => {
    mountOpacity.value = withDelay(150, withTiming(1, { duration: 360, easing: Easing.out(Easing.cubic) }));
    mountY.value = withDelay(150, withSpring(0, { damping: 18, stiffness: 130 }));
  }, []);

  const style = useAnimatedStyle(() => ({
    opacity: mountOpacity.value,
    transform: [{ translateY: mountY.value }],
  }));

  return (
    <Animated.View style={[s.quickRow, style]}>
      <ClickSpark onPress={onQuick5} disabled={quick5.length === 0}>
        <Pressable
          style={[s.quickBtn, quick5.length === 0 && { opacity: 0.45 }]}
          onPress={onQuick5}
          disabled={quick5.length === 0}
        >
          <Text style={s.quickTitle}>תרגול 5</Text>
          <Text style={s.quickSub}>5 אינטראקציות ממוקדות</Text>
        </Pressable>
      </ClickSpark>
      <ClickSpark onPress={onQuick10} disabled={quick10.length === 0}>
        <Pressable
          style={[s.quickBtn, quick10.length === 0 && { opacity: 0.45 }]}
          onPress={onQuick10}
          disabled={quick10.length === 0}
        >
          <Text style={s.quickTitle}>תרגול 10</Text>
          <Text style={s.quickSub}>10 אינטראקציות לחיזוק</Text>
        </Pressable>
      </ClickSpark>
    </Animated.View>
  );
}

export function RuntimePracticeCenterScreen({ runtimeBase }: { runtimeBase: RuntimeBase }) {
  const { slug, uid, token } = useLocalSearchParams<{ slug: string; uid: string; token?: string }>();
  const safeSlug = typeof slug === 'string' ? slug : '';
  const safeUid = typeof uid === 'string' ? uid : '';
  const runtimeSession = { uid: safeUid, token: token ? String(token) : undefined };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Awaited<ReturnType<typeof loadPracticeCenterData>> | null>(null);

  const screenOpacity = useSharedValue(0);
  const screenY = useSharedValue(14);

  const load = useCallback(async () => {
    if (!safeSlug || !safeUid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await loadPracticeCenterData(safeSlug, safeUid);
      setData(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינת מרכז התרגול');
    } finally {
      setLoading(false);
    }
  }, [safeSlug, safeUid]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!loading) {
      screenOpacity.value = withTiming(1, { duration: 440, easing: Easing.out(Easing.cubic) });
      screenY.value = withSpring(0, { damping: 20, stiffness: 110 });
    }
  }, [loading]);

  const screenStyle = useAnimatedStyle(() => ({
    opacity: screenOpacity.value,
    transform: [{ translateY: screenY.value }],
  }));

  const topicLessonIds = useMemo(
    () => data?.lessons.filter((l) => l.status !== 'locked').sort((a, b) => a.order_index - b.order_index).map((l) => l.id) ?? [],
    [data],
  );

  const goSession = (lessonIds: string[], mode: string, targetInteractions?: number) => {
    if (!safeSlug || !safeUid || lessonIds.length === 0) return;
    router.push(
      buildRuntimePracticeSessionPath(runtimeBase, safeSlug, runtimeSession, {
        mode,
        lesson_ids: lessonIds.join(','),
        target: targetInteractions ? String(targetInteractions) : undefined,
      }) as any,
    );
  };

  if (!safeSlug || !safeUid) {
    return <RuntimeSessionGuard runtimeBase={runtimeBase} slug={safeSlug} />;
  }

  if (loading) {
    return (
      <View style={s.center}>
        <PulseLoader color={PRIMARY} />
      </View>
    );
  }

  if (error || !data) {
    return (
      <View style={s.center}>
        <Text style={s.errorText}>{error ?? 'שגיאה בטעינה'}</Text>
        <Pressable style={s.retryBtn} onPress={load}><Text style={s.retryText}>נסה שוב</Text></Pressable>
      </View>
    );
  }

  const weakLessons = data.weakLessonIds.slice(0, 3);
  const quick5 = pickQuickLessons(data.lessons, 5);
  const quick10 = pickQuickLessons(data.lessons, 10);
  const unlockedLessonsCount = data.lessons.filter((l) => l.status !== 'locked').length;
  const readyForExam = data.hasAttemptHistory && unlockedLessonsCount >= 2;

  return (
    <ScrollView style={s.root} contentContainerStyle={s.content}>
      <Animated.View style={[{ gap: 14 }, screenStyle]}>
        <View style={s.topContext}>
          <Pressable
            style={s.backBtn}
            onPress={() => router.back()}
            accessibilityRole="button"
            accessibilityLabel="חזרה"
          >
            <Text style={s.backBtnText}>← חזרה למסלול</Text>
          </Pressable>
          <Text style={s.eyebrow}>KURSI LEARN · PRACTICE</Text>
        </View>

        <View style={s.hero}>
          <Text style={s.title}>מרכז תרגול</Text>
          <Text style={s.subtitle}>בחרו את סוג התרגול שמתאים לכם עכשיו והמשיכו לצבור ביטחון לפני השיעור הבא.</Text>
          <View style={s.heroStatsRow}>
            <View style={s.heroStat}>
              <Text style={s.heroStatValue}>{unlockedLessonsCount}</Text>
              <Text style={s.heroStatLabel}>שיעורים זמינים</Text>
            </View>
            <View style={s.heroStat}>
              <Text style={[s.heroStatValue, { color: SUCCESS }]}>{data.hasAttemptHistory ? 'מוכן' : 'בנייה'}</Text>
              <Text style={s.heroStatLabel}>פרופיל חולשות</Text>
            </View>
          </View>
        </View>

        <BubbleMenu
          items={['תרגול חולשות', 'סקירת טעויות', 'מצב מבחן']}
          color={PRIMARY}
          onSelect={(item) => {
            if (item === 'מצב מבחן') {
              router.push(buildRuntimeExamPath(runtimeBase, safeSlug, runtimeSession) as any);
            } else if (item === 'סקירת טעויות') {
              router.push(buildRuntimeReviewPath(runtimeBase, safeSlug, runtimeSession) as any);
            } else {
              goSession((weakLessons.length > 0 ? weakLessons : topicLessonIds.slice(0, 2)), 'weakness');
            }
          }}
        />

        <ModeCard
          icon="◎"
          title="תרגול לפי חולשה"
          subtitle={data.hasAttemptHistory ? 'מבוסס על תוצאות תרחישים קודמות (Weak/Risky + איכות ביצוע).' : 'אין עדיין היסטוריה אישית, נתחיל בתרגול כיסוי נושאים.'}
          cta="התחל תרגול חולשות"
          badge="מומלץ עכשיו"
          mountDelay={60}
          onPress={() => goSession((weakLessons.length > 0 ? weakLessons : topicLessonIds.slice(0, 2)), 'weakness')}
        />

        <ModeCard
          icon="≡"
          title="תרגול לפי נושא"
          subtitle="בחר נושאי שיעור ותתרגל רק אותם."
          cta="התחל לפי נושא"
          mountDelay={120}
          onPress={() => goSession(topicLessonIds.slice(0, 2), 'topic')}
          disabled={topicLessonIds.length === 0}
        />

        <QuickRow
          quick5={quick5}
          quick10={quick10}
          onQuick5={() => goSession(quick5, 'quick', 5)}
          onQuick10={() => goSession(quick10, 'quick', 10)}
        />

        <ModeCard
          icon="▣"
          title="מצב מבחן"
          subtitle="בדיקת מוכנות ממוקדת: סשן סגור של 10 שאלות עם סיכום חוזקות וחולשות."
          cta={readyForExam ? 'התחל בדיקת מוכנות' : 'התחל בכל זאת (מומלץ אחרי תרגול)'}
          badge={readyForExam ? 'מוכן למבחן' : undefined}
          mountDelay={180}
          onPress={() => router.push(buildRuntimeExamPath(runtimeBase, safeSlug, runtimeSession) as any)}
        />

        <ModeCard
          icon="↺"
          title="סקירת טעויות"
          subtitle="מסך תיקון טעויות עם הסברים ועזרה ממוקדת לפי ניסיונות חלשים/מסוכנים."
          cta="פתח סקירת טעויות"
          mountDelay={240}
          onPress={() => router.push(buildRuntimeReviewPath(runtimeBase, safeSlug, runtimeSession) as any)}
        />

        <ModeCard
          icon="↩"
          title="מסלול חזרה ממוקדת"
          subtitle={data.hasAttemptHistory
            ? 'מסלול חזרה שמתחיל מהטעויות האחרונות, סוגר פערים ואז ממליץ מה הצעד הבא.'
            : 'מסלול זה צריך נתוני תרגול אמיתיים. התחילו בתרגול קצר וחזרו לכאן.'}
          cta={data.hasAttemptHistory ? 'פתח מסלול חזרה' : 'בנו קודם היסטוריית תרגול'}
          mountDelay={270}
          onPress={() => router.push(buildRuntimeReversePath(runtimeBase, safeSlug, runtimeSession) as any)}
        />

        <MagicBento blocks={[
          { icon: '◎', title: 'תרגול ממוקד', body: 'תרגול אדפטיבי לפי חולשות כדי לשפר מוכנות בפחות זמן.', accent: PRIMARY },
          { icon: '↺', title: 'מרכז תיקון', body: 'צפייה בטעויות, הסברים והפעלה מהירה של תיקון נקודתי.', accent: '#10B981' },
        ]} />

        <ModeCard
          icon="↩"
          title="טעויות אחרונות"
          subtitle={data.hasMistakes ? 'תרגול ממוקד תרחישים בהם טעיתם לאחרונה.' : 'אין טעויות מתועדות עדיין — נעבור לתרגול חולשות כללי.'}
          cta="תרגלו טעויות אחרונות"
          mountDelay={330}
          onPress={() => goSession((data.hasMistakes ? data.recentMistakeLessonIds : weakLessons).slice(0, 3), 'mistakes')}
        />

        {!data.hasAttemptHistory && (
          <View style={s.emptyState}>
            <Text style={s.emptyTitle}>עדיין אין היסטוריית תרגול אישית</Text>
            <Text style={s.emptySub}>מרכז התרגול משתמש כרגע בכיסוי נושאים כללי עד שיצטברו מספיק ניסיונות אישיים.</Text>
          </View>
        )}
      </Animated.View>
    </ScrollView>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },
  content: { padding: 20, maxWidth: 640, width: '100%', alignSelf: 'center' as any, paddingTop: 30, paddingBottom: 40 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: BG },
  topContext: { gap: 10, alignItems: 'flex-end' },
  backBtn: { borderWidth: 1, borderColor: BORDER, backgroundColor: '#121126', borderRadius: 999, paddingVertical: 8, paddingHorizontal: 14 },
  backBtnText: { color: TEXT_MED, fontFamily: theme.fonts.medium, fontSize: 12 },
  eyebrow: { color: PRIMARY, fontFamily: theme.fonts.bold, fontSize: 12, letterSpacing: 1 },
  hero: { backgroundColor: '#121126', borderRadius: 18, borderWidth: 1, borderColor: BORDER, padding: 16, gap: 10 },
  title: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 30 },
  subtitle: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 14, lineHeight: 22 },
  heroStatsRow: { flexDirection: 'row-reverse', gap: 10 },
  heroStat: { flex: 1, backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 12, paddingVertical: 10, paddingHorizontal: 12, alignItems: 'flex-end' },
  heroStatValue: { color: PRIMARY, fontFamily: theme.fonts.bold, fontSize: 20 },
  heroStatLabel: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 12 },
  modeCard: { backgroundColor: SURFACE, borderColor: BORDER, borderWidth: 1, borderRadius: 14, padding: 16, gap: 8 },
  modeHeader: { flexDirection: 'row-reverse', justifyContent: 'space-between', alignItems: 'center' },
  modeIcon: { fontSize: 20 },
  badge: { backgroundColor: PRIMARY + '24', borderColor: PRIMARY + '44', borderWidth: 1, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 4 },
  badgeText: { color: PRIMARY, fontFamily: theme.fonts.bold, fontSize: 11 },
  modeTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 18, textAlign: 'right' },
  modeSubtitle: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 13, textAlign: 'right', lineHeight: 20 },
  modeCta: { color: PRIMARY, fontFamily: theme.fonts.bold, fontSize: 14, textAlign: 'right', marginTop: 4 },
  quickRow: { flexDirection: 'row-reverse', gap: 10 },
  quickBtn: { flex: 1, backgroundColor: SURFACE, borderColor: BORDER, borderWidth: 1, borderRadius: 14, padding: 14, alignItems: 'center' },
  quickTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 18 },
  quickSub: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 12 },
  retryBtn: { backgroundColor: PRIMARY, paddingHorizontal: 16, paddingVertical: 10, borderRadius: 10, marginTop: 10 },
  retryText: { color: '#fff', fontFamily: theme.fonts.bold },
  errorText: { color: '#fff', fontFamily: theme.fonts.bold },
  emptyState: { marginTop: 4, padding: 14, borderRadius: 12, borderWidth: 1, borderColor: BORDER, backgroundColor: '#141326' },
  emptyTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 14, textAlign: 'right' },
  emptySub: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 12, textAlign: 'right', marginTop: 5 },
});
